/* eslint-disable react/no-array-index-key */
import React from 'react';

import type { A11yTextType } from '../../../../common/components/A11yTextWrapper/A11yTextWrapper';
import type { PriceType } from '../../../../common/models/priceData/priceData';
import renderProductPriceGroups, {
    defineProductPriceGroup,
} from '../../../../ecommerce/utils/renderProductPriceGroups';
import type { RecipeItemState } from '../../../../recipe/models';
import cartItemPrePurchasePriceLabels from '../../CartItemProductListing/CartItemPrePurchaseProductPresentationSpec';
import { getPrePurchaseProductPriceGroups } from '../PrepurchaseProductPriceLabels/PrepurchaseProductPriceLabels';
import type { RecipeProductListingItemData } from './RecipeProductListingItem.types';

type RecipeItemPriceLabelsParameters = {
    product: RecipeProductListingItemData;
    recipeItem: RecipeItemState;
    priceType: PriceType;
    a11yTextType: A11yTextType;
    isB2B: boolean;
    isUserMember: boolean;
    arePILPricesEnabled: boolean;
    isPILPricesLogicEnabled: boolean;
};

const RecipeProductListingItemPriceLabels = React.memo(
    (
        props: RecipeItemPriceLabelsParameters & {
            type: 'primary' | 'secondary' | 'total';
        },
    ) => {
        const recipeItemPriceLabels = getPriceGroups(props);
        return recipeItemPriceLabels[props.type].render;
    },
);

export default RecipeProductListingItemPriceLabels;

export const getRecipeProductListingItemPriceA11yText = (
    params: RecipeItemPriceLabelsParameters,
) => {
    const prepurchaseProductPriceLabels = getPriceGroups(params);
    return {
        primaryText: prepurchaseProductPriceLabels.primary.a11y,
        secondaryText: prepurchaseProductPriceLabels.secondary.a11y,
        totalText: prepurchaseProductPriceLabels.total.a11y,
    };
};

const getPriceGroups = (params: RecipeItemPriceLabelsParameters) => {
    const base = getPrePurchaseProductPriceGroups(params);

    return renderProductPriceGroups(
        () => `${params.product.identifier}}`,
        [
            defineProductPriceGroup({
                type: 'primary',
                specSet: ['primaryPrice'],
                createLabel: base.definitions.primary.createLabel,
            }),
            defineProductPriceGroup({
                type: 'secondary',
                specSet: [
                    'maxPromoUsage',
                    'promoComparativePrice',
                    'originalPrice',
                    'comparativePrice',
                    'historicalPrice',
                ],
                createLabel: base.definitions.secondary.createLabel,
            }),
            defineProductPriceGroup({
                type: 'total',
                specSet: ['totalPrice', 'excludingTax'],
                createLabel: (spec) => {
                    return cartItemPrePurchasePriceLabels.getPrePurchaseTotalPriceLabelForSpec({
                        product: params.product,
                        priceType: params.priceType,
                        a11yTextType: params.a11yTextType,
                        isB2B: params.isB2B,
                        arePILPricesEnabled: params.arePILPricesEnabled,
                        isPILPricesLogicEnabled: params.isPILPricesLogicEnabled,
                        cartItemPromotion: undefined, // Recipe items do not have promotions
                        isUserMember: params.isUserMember,
                        cartItem: {
                            productId: params.product.identifier,
                            variantId: undefined,
                            totalPriceData: params.recipeItem.totalPriceData,
                            id: params.recipeItem.id,
                            recycleFeeTotalPriceData: undefined, // Recipe items do not have recycle fees
                        },
                    })(spec);
                },
            }),
        ],
    );
};
